import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '@/components/DataTable';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { EditOnly } from '@/components/EditOnly';
import { DatePicker } from '@/components/DatePicker';
import { Select } from '@/components/Select';
import {
  getCampaigns,
  createCampaign,
  updateCampaign,
  deleteCampaign,
  archiveCampaign,
} from '@/api/campaigns';
import type { Campaign, CreateCampaignInput, UpdateCampaignInput } from '@/api/types';
import { parseUtc, effectiveTimezone } from '@/lib/datetime';
import { getApps } from '@/api/apps';

type ModalMode = 'create' | 'edit' | 'delete' | 'archive' | null;

interface FormData {
  app_id: string;
  name: string;
  status: 'active' | 'paused' | 'completed';
  start_date: string;
  end_date: string;
}

const initialFormData: FormData = {
  app_id: '',
  name: '',
  status: 'active',
  start_date: '',
  end_date: '',
};

// Format date for display
function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  const date = parseUtc(dateStr);
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: effectiveTimezone(),
  }).format(date);
}

export function Campaigns() {
  const queryClient = useQueryClient();
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [formError, setFormError] = useState('');
  const [hideTest, setHideTest] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('');

  // Query for fetching campaigns
  const {
    data: campaigns = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ['campaigns', statusFilter],
    queryFn: () => getCampaigns({ status: statusFilter || undefined }),
  });
  const { data: apps = [] } = useQuery({ queryKey: ['apps'], queryFn: getApps });

  const filteredCampaigns = useMemo(() => {
    if (!hideTest) return campaigns;
    return campaigns.filter((c) => !c.name.toLowerCase().includes('test'));
  }, [campaigns, hideTest]);

  // Mutation for creating campaign
  const createMutation = useMutation({
    mutationFn: (data: CreateCampaignInput) => createCampaign(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for updating campaign
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCampaignInput }) =>
      updateCampaign(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for deleting campaign
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  // Mutation for archiving a campaign (one-way; cascades to its ads)
  const archiveMutation = useMutation({
    mutationFn: (id: string) => archiveCampaign(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      queryClient.invalidateQueries({ queryKey: ['ads'] });
      closeModal();
    },
    onError: (err: Error) => {
      setFormError(err.message);
    },
  });

  const openArchiveModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setFormError('');
    setModalMode('archive');
  };

  const handleArchive = () => {
    if (selectedCampaign) {
      archiveMutation.mutate(selectedCampaign.id);
    }
  };

  const openCreateModal = () => {
    setFormData({ ...initialFormData, app_id: apps[0]?.id || '' });
    setFormError('');
    setSelectedCampaign(null);
    setModalMode('create');
  };

  const openEditModal = (campaign: Campaign) => {
    setFormData({
      app_id: '',
      name: campaign.name,
      status: campaign.status,
      start_date: campaign.start_date ? campaign.start_date.split('T')[0] : '',
      end_date: campaign.end_date ? campaign.end_date.split('T')[0] : '',
    });
    setFormError('');
    setSelectedCampaign(campaign);
    setModalMode('edit');
  };

  const openDeleteModal = (campaign: Campaign) => {
    setSelectedCampaign(campaign);
    setFormError('');
    setModalMode('delete');
  };

  const closeModal = () => {
    setModalMode(null);
    setSelectedCampaign(null);
    setFormData(initialFormData);
    setFormError('');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!formData.name.trim() || (modalMode === 'create' && !formData.app_id)) {
      setFormError('App and name are required');
      return;
    }

    const payload = {
      name: formData.name.trim(),
      status: formData.status,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
    };

    if (modalMode === 'create') {
      createMutation.mutate({ ...payload, app_id: formData.app_id });
    } else if (modalMode === 'edit' && selectedCampaign) {
      updateMutation.mutate({
        id: selectedCampaign.id,
        data: payload,
      });
    }
  };

  const handleDelete = () => {
    if (selectedCampaign) {
      deleteMutation.mutate(selectedCampaign.id);
    }
  };

  const columns = [
    {
      key: 'name',
      label: 'Name',
      render: (campaign: Campaign) => (
        <span className="font-medium text-content">{campaign.name}</span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (campaign: Campaign) => {
        if (campaign.archived_at) {
          return (
            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-surface-muted text-content-subtle">
              Archived
            </span>
          );
        }
        const statusStyles = {
          active: 'bg-success-soft text-success',
          paused: 'bg-surface-muted text-content-muted',
          completed: 'bg-info-soft text-info',
        };
        const statusLabels = {
          active: 'Active',
          paused: 'Paused',
          completed: 'Completed',
        };
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusStyles[campaign.status]}`}
          >
            {statusLabels[campaign.status]}
          </span>
        );
      },
    },
    {
      key: 'start_date',
      label: 'Start Date',
      render: (campaign: Campaign) => (
        <span className="text-content-muted text-sm">
          {formatDate(campaign.start_date)}
        </span>
      ),
    },
    {
      key: 'end_date',
      label: 'End Date',
      render: (campaign: Campaign) => (
        <span className="text-content-muted text-sm">
          {formatDate(campaign.end_date)}
        </span>
      ),
    },
    {
      key: 'created_at',
      label: 'Created',
      render: (campaign: Campaign) => (
        <span className="text-content-muted text-sm">
          {formatDate(campaign.created_at)}
        </span>
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (campaign: Campaign) => (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(campaign);
            }}
            className="text-content-subtle hover:text-content-muted transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
          </button>
          {!campaign.archived_at && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openArchiveModal(campaign);
              }}
              className="text-content-subtle hover:text-content-muted transition-colors"
              title="Archive campaign and its ads"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8M10 12h4" />
              </svg>
            </button>
          )}
          <button
            onClick={(e) => {
              e.stopPropagation();
              openDeleteModal(campaign);
            }}
            className="text-content-subtle hover:text-danger transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  if (error) {
    return (
      <div className="bg-danger-soft border border-danger/30 rounded-lg p-4">
        <p className="text-sm text-danger">
          Failed to load campaigns:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-content">Campaigns</h1>
          <p className="text-sm text-content-subtle mt-1">
            Manage your advertising campaigns
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="w-40">
            <Select
              value={statusFilter}
              onChange={(v) => setStatusFilter(v)}
              placeholder="All"
              options={[
                { value: '', label: 'All' },
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
                { value: 'completed', label: 'Completed' },
                { value: 'archived', label: 'Archived' },
              ]}
            />
          </div>
          <label className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={hideTest}
              onChange={(e) => setHideTest(e.target.checked)}
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span className="text-sm text-content-muted">Hide test</span>
          </label>
          <EditOnly>
            <Button onClick={openCreateModal}>
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Create Campaign
            </Button>
          </EditOnly>
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredCampaigns}
        loading={isLoading}
        onRowClick={openEditModal}
        keyExtractor={(campaign) => campaign.id}
      />

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modalMode === 'create' || modalMode === 'edit'}
        onClose={closeModal}
        title={modalMode === 'create' ? 'Create Campaign' : 'Edit Campaign'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          {modalMode === 'create' && (
            <div>
              <label className="block text-sm font-medium text-content-muted">App</label>
              <Select
                value={formData.app_id}
                onChange={(v) => setFormData((prev) => ({ ...prev, app_id: v }))}
                className="mt-1"
                placeholder="— select —"
                options={apps.map((app) => ({ value: app.id, label: app.name }))}
              />
            </div>
          )}

          <div>
            <label
              htmlFor="name"
              className="block text-sm font-medium text-content-muted"
            >
              Campaign Name
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, name: e.target.value }))
              }
              className="mt-1 block w-full px-3 py-2 border border-border-strong rounded-md shadow-sm placeholder-content-subtle focus:outline-none focus:ring-primary focus:border-primary sm:text-sm"
              placeholder="Summer Sale Campaign"
            />
          </div>

          <div>
            <label
              htmlFor="status"
              className="block text-sm font-medium text-content-muted"
            >
              Status
            </label>
            <Select
              value={formData.status}
              onChange={(v) =>
                setFormData((prev) => ({
                  ...prev,
                  status: v as 'active' | 'paused' | 'completed',
                }))
              }
              className="mt-1"
              options={[
                { value: 'active', label: 'Active' },
                { value: 'paused', label: 'Paused' },
                { value: 'completed', label: 'Completed' },
              ]}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <DatePicker
              id="start_date"
              label="Start Date"
              value={formData.start_date}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, start_date: value }))
              }
            />
            <DatePicker
              id="end_date"
              label="End Date"
              value={formData.end_date}
              onChange={(value) =>
                setFormData((prev) => ({ ...prev, end_date: value }))
              }
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={createMutation.isPending || updateMutation.isPending}
            >
              {modalMode === 'create' ? 'Create Campaign' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={modalMode === 'delete'}
        onClose={closeModal}
        title="Delete Campaign"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <p className="text-sm text-content-muted">
            Are you sure you want to delete{' '}
            <span className="font-medium text-content">
              {selectedCampaign?.name}
            </span>
            ? This action cannot be undone and will also delete all ads
            associated with this campaign.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={handleDelete}
              loading={deleteMutation.isPending}
            >
              Delete
            </Button>
          </div>
        </div>
      </Modal>

      {/* Archive Confirmation Modal */}
      <Modal
        isOpen={modalMode === 'archive'}
        onClose={closeModal}
        title="Archive Campaign"
        size="sm"
      >
        <div className="space-y-4">
          {formError && (
            <div className="p-3 bg-danger-soft border border-danger/30 rounded-md">
              <p className="text-sm text-danger">{formError}</p>
            </div>
          )}

          <p className="text-sm text-content-muted">
            Archive{' '}
            <span className="font-medium text-content">
              {selectedCampaign?.name}
            </span>
            ? This also archives all of its ads and is irreversible. Archived
            campaigns are hidden from the list and stop serving, but stay
            available for statistics.
          </p>

          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleArchive}
              loading={archiveMutation.isPending}
            >
              Archive
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
